import { useCallback, useEffect, useState, type FormEvent } from "react";
import { ConfirmDialog, FormField, fieldAria } from "../components/Dialog";
import { Icon } from "../components/Icon";
import { Note, PageHeader, Panel } from "../components/ui";
import type { PushConfig, PushDevice } from "../data/provider";
import { useI18n, type MessageKey } from "../i18n";
import { errorKey } from "../lib/errors";
import { PASSWORD_MAX, PASSWORD_MIN, passwordIssue, passwordStrength, repeatIssue, type PasswordIssue } from "../lib/password";
import {
  currentEndpoint,
  currentSubscriptionKey,
  pushEnvironment,
  PushClientError,
  subscribeThisDevice,
  unpad,
  unsubscribeThisDevice,
  type PushEnvironment,
} from "../push/client";
import { useSession } from "../state/session";

/** Injected in tests; the browser implementation by default. */
export type PushBrowser = {
  environment: () => PushEnvironment;
  currentEndpoint: () => Promise<string | null>;
  /** VAPID key of this browser's subscription (to detect rotated server keys); optional in tests. */
  subscriptionKey?: () => Promise<string | null>;
  subscribe: typeof subscribeThisDevice;
  unsubscribe: typeof unsubscribeThisDevice;
};
const BROWSER: PushBrowser = {
  environment: () => pushEnvironment(),
  currentEndpoint,
  subscriptionKey: currentSubscriptionKey,
  subscribe: subscribeThisDevice,
  unsubscribe: unsubscribeThisDevice,
};

const CLIENT_ERRORS: Record<PushClientError["code"], MessageKey> = {
  denied: "push.err.denied",
  no_service_worker: "push.err.noSw",
  subscribe_failed: "push.err.subscribeFailed",
};

/** "Profilim": account details and the user's own notification devices (ADR 0019). Every role. */
export function ProfilePage({ browser = BROWSER }: { browser?: PushBrowser }) {
  const { t } = useI18n();
  const { user } = useSession();
  return (
    <>
      <PageHeader title={t("profile.title")} subtitle={t("profile.subtitle")} />
      <Panel title={t("profile.account")}>
        <dl className="profile-account">
          <dt>{t("profile.name")}</dt>
          <dd>{user?.displayName || t("common.empty")}</dd>
          <dt>{t("profile.email")}</dt>
          <dd>{user?.email || t("common.empty")}</dd>
        </dl>
      </Panel>
      <PasswordPanel />
      <PushPanel browser={browser} />
    </>
  );
}

const ISSUE_KEYS: Record<PasswordIssue, MessageKey> = {
  required: "password.err.required",
  tooShort: "password.err.tooShort",
  tooLong: "password.err.tooLong",
  sameAsEmail: "password.err.sameAsEmail",
  repeatRequired: "password.err.repeatRequired",
  mismatch: "password.err.mismatch",
};

/** "Şifre": set or change the console password; works for accounts that so far only used e-mail links. */
function PasswordPanel() {
  const { t } = useI18n();
  const { provider, user } = useSession();
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const [show, setShow] = useState(false);
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: "ok" | "error"; key: MessageKey } | null>(null);

  const issue = passwordIssue(password, user?.email ?? "");
  const issue2 = repeatIssue(password, repeat);
  const newError = touched && issue ? t(ISSUE_KEYS[issue], { min: PASSWORD_MIN, max: PASSWORD_MAX }) : null;
  const repeatError = touched && !issue && issue2 ? t(ISSUE_KEYS[issue2]) : null;
  const strength = password ? passwordStrength(password) : null;
  const hint = (
    <>
      {t("password.rules", { min: PASSWORD_MIN, max: PASSWORD_MAX })}
      {strength && (
        <>
          {" "}
          <span className={`password-strength is-${strength}`} data-strength={strength}>
            {t(`password.strength.${strength}`)}
          </span>
        </>
      )}
    </>
  );

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setTouched(true);
    setMessage(null);
    if (issue || issue2) return;
    setBusy(true);
    try {
      await provider.changePassword(password);
      setPassword("");
      setRepeat("");
      setTouched(false);
      setShow(false);
      setMessage({ tone: "ok", key: "password.updated" });
    } catch (err) {
      setMessage({ tone: "error", key: errorKey(err) });
    } finally {
      setBusy(false);
    }
  };

  const type = show ? "text" : "password";
  return (
    <Panel title={t("password.title")}>
      <form className="stack password-form" onSubmit={(e) => void submit(e)} noValidate>
        <p className="panel-text">{t("password.intro")}</p>
        <FormField id="password-new" label={t("password.new")} hint={hint} error={newError}>
          <input
            {...fieldAria("password-new", { hint, error: newError })}
            type={type}
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </FormField>
        <FormField id="password-repeat" label={t("password.repeat")} error={repeatError}>
          <input
            {...fieldAria("password-repeat", { error: repeatError })}
            type={type}
            autoComplete="new-password"
            value={repeat}
            onChange={(e) => setRepeat(e.target.value)}
          />
        </FormField>
        <label className="check-row">
          <input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} />
          <span>{t("password.show")}</span>
        </label>
        {message && (
          <p className={message.tone === "ok" ? "form-success" : "form-error"} role={message.tone === "ok" ? "status" : "alert"}>
            {t(message.key)}
          </p>
        )}
        <div>
          <button type="submit" className="button button-primary" disabled={busy}>
            {busy ? t("common.working") : t("password.submit")}
          </button>
        </div>
      </form>
    </Panel>
  );
}

function PushPanel({ browser }: { browser: PushBrowser }) {
  const { t, locale } = useI18n();
  const { provider, platformAdmin } = useSession();
  const [config, setConfig] = useState<PushConfig | null | "error">(null);
  const [devices, setDevices] = useState<PushDevice[]>([]);
  const [here, setHere] = useState<string | null>(null);
  const [hereKey, setHereKey] = useState<string | null>(null);
  const [env] = useState<PushEnvironment>(() => browser.environment());
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: "ok" | "error"; text: string } | null>(null);
  const [confirm, setConfirm] = useState<PushDevice | null>(null);
  // "Remember me" unticked = probably a shared device: no alarm pushes here (ADR 0020).
  const remembered = provider.sessionRemembered();

  const reload = useCallback(async () => {
    try {
      const [c, list, endpoint, key] = await Promise.all([
        provider.getPushConfig(),
        provider.listPushDevices(),
        browser.currentEndpoint(),
        browser.subscriptionKey ? browser.subscriptionKey() : Promise.resolve(null),
      ]);
      setHereKey(key);
      setConfig(c);
      setDevices(list);
      setHere(endpoint);
    } catch {
      setConfig("error");
    }
  }, [provider, browser]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const fmt = (iso: string) => new Date(iso).toLocaleString(locale, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  const thisDevice = here ? devices.find((d) => d.endpoint === here) ?? null : null;
  // QA-7B-04: this browser's subscription was made with a different server key (keys rotated).
  const stale = !!here && !!hereKey && config !== null && config !== "error" && !!config.publicKey && hereKey !== unpad(config.publicKey);
  const fail = (e: unknown) => setMessage({ tone: "error", text: t(e instanceof PushClientError ? CLIENT_ERRORS[e.code] : errorKey(e)) });

  const run = async (fn: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    setMessage(null);
    try {
      await fn();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
      void reload();
    }
  };

  // The permission prompt opens only here, from the user's click.
  const enable = () =>
    run(async () => {
      if (config === null || config === "error" || !config.publicKey) return;
      const old = here;
      const sub = await browser.subscribe(config.publicKey);
      await provider.savePushSubscription(sub);
      // Renewed after a key rotation: the browser got a new endpoint; drop the dead row of the old one.
      const dead = old && old !== sub.endpoint ? devices.find((d) => d.endpoint === old) : undefined;
      if (dead) await provider.removePushDevice(dead.id).catch(() => undefined);
      setMessage({ tone: "ok", text: t("push.enabled") });
    });

  const disableHere = () =>
    run(async () => {
      const endpoint = await browser.unsubscribe();
      const row = devices.find((d) => d.endpoint === (endpoint ?? here));
      if (row) await provider.removePushDevice(row.id);
      setMessage({ tone: "ok", text: t("push.disabled") });
    });

  const remove = (d: PushDevice) =>
    run(async () => {
      setConfirm(null);
      if (d.endpoint === here) await browser.unsubscribe();
      await provider.removePushDevice(d.id);
    });

  const test = () =>
    run(async () => {
      const r = await provider.sendTestPush();
      setMessage({ tone: r.failed > 0 || r.sent === 0 ? "error" : "ok", text: t("push.testResult", { sent: r.sent, failed: r.failed, removed: r.removed }) });
    });

  // Server setup (VAPID keys, docs path) is for Expartise platform admins; everyone else gets the user message.
  const admin = platformAdmin;
  let body;
  if (config === null) {
    body = (
      <div className="loading" role="status">
        <span className="spinner" aria-hidden="true" />
        {t("common.loading")}
      </div>
    );
  } else if (config === "error") {
    body = (
      <div className="push-state" role="alert">
        <p>{t("push.configError")}</p>
        <button type="button" className="button button-secondary" onClick={() => void reload()}>
          {t("common.retry")}
        </button>
      </div>
    );
  } else {
    body = (
      <>
        <p className="push-intro">{t("push.intro")}</p>
        {!config.enabled ? (
          <Note>{t(admin ? "push.disabledAdmin" : "push.disabledUser")}</Note>
        ) : env !== "ok" ? (
          <Note>{t(`push.env.${env}`)}</Note>
        ) : !remembered && !thisDevice ? (
          <div className="push-here" role="note">
            <Icon name="lock" size={18} />
            <span>{t("push.notRemembered")}</span>
          </div>
        ) : stale && remembered ? (
          // Subscribed with another (rotated) VAPID key: this device gets nothing until it subscribes again.
          <div className="push-here" role="alert">
            <Icon name="alert" size={18} />
            <span>{t("push.stale")}</span>
            <button type="button" className="button button-primary" disabled={busy} onClick={() => void enable()}>
              {t("push.renew")}
            </button>
          </div>
        ) : (
          <div className="push-here">
            <Icon name="bell" size={18} />
            <span>{thisDevice ? t("push.thisDeviceOn") : t("push.thisDeviceOff")}</span>
            {thisDevice ? (
              <button type="button" className="button button-secondary" disabled={busy} onClick={() => void disableHere()}>
                {t("push.disable")}
              </button>
            ) : (
              <button type="button" className="button button-primary" disabled={busy} onClick={() => void enable()}>
                {t("push.enable")}
              </button>
            )}
            {!thisDevice && <span className="push-hint">{t("push.permissionNote")}</span>}
          </div>
        )}
        {message && (
          <p className={message.tone === "ok" ? "push-message is-ok" : "form-error"} role={message.tone === "ok" ? "status" : "alert"}>
            {message.text}
          </p>
        )}
        <h3 className="push-subtitle">{t("push.devices")}</h3>
        {devices.length === 0 ? (
          <p className="push-empty">{t("push.noDevices")}</p>
        ) : (
          <ul className="push-devices">
            {devices.map((d) => (
              <li key={d.id}>
                <div className="push-device-name">
                  {d.label || t("common.empty")}
                  {d.endpoint === here && <span className="badge tone-accent">{t("push.thisDevice")}</span>}
                </div>
                <div className="push-device-meta">
                  <span>{t("push.added", { date: fmt(d.createdAt) })}</span>
                  {d.lastSuccessAt && <span>{t("push.lastOk", { date: fmt(d.lastSuccessAt) })}</span>}
                  {d.failureCount > 0 && <span className="push-device-fail">{t("push.failures", { n: d.failureCount })}</span>}
                </div>
                <button type="button" className="button button-sm button-danger-text" disabled={busy} onClick={() => setConfirm(d)}>
                  {t("push.remove")}
                </button>
              </li>
            ))}
          </ul>
        )}
        {config.enabled && devices.length > 0 && (
          <button type="button" className="button button-secondary" disabled={busy} onClick={() => void test()}>
            {t("push.test")}
          </button>
        )}
      </>
    );
  }

  return (
    <Panel title={t("push.title")}>
      <div className="push-panel">{body}</div>
      {confirm && (
        <ConfirmDialog
          title={t("push.removeTitle")}
          confirmLabel={t("push.remove")}
          danger
          onConfirm={() => remove(confirm)}
          onClose={() => setConfirm(null)}
        >
          {t("push.removeText", { name: confirm.label || t("common.empty") })}
        </ConfirmDialog>
      )}
    </Panel>
  );
}
